/**
 * Get Access Page — Plan Selection
 *
 * Flow:
 *   1. Public visitor lands here from marketing pages.
 *   2. Fetches the public plans from /bff/plans (public endpoint).
 *   3. Single tier since 2026-07-31 — the only choice is billing period (monthly / annual).
 *   4. Clicking "Get Access" — if not authenticated, CIAM login first, returns here with ?plan=<code>.
 *      If authenticated with no active subscription, POST /payment/checkout and redirect to Stripe.
 *   5. After Stripe completes, the user lands on /welcome (configured as CHECKOUT_SUCCESS_URL).
 *
 * Existing-subscriber behavior:
 *   - If user arrives here with subscription.status === 'active' (or past_due), redirect to /welcome.
 *     Matches the "DTA-only dashboard access" rule — paid users don't see the dashboard via
 *     the public site; they're guided to install the DTA.
 */

import React, { useState, useEffect } from 'react';
import usePageTitle from '../../hooks/usePageTitle';
import { Navigate, useSearchParams, Link } from 'react-router-dom';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { API_BASE_URL, fetchWithCsrf } from '../../config/api';
import SiteHeader from '../../components/site/SiteHeader';
import SiteFooter from '../../components/site/SiteFooter';
import { ROUTES } from '../../components/site/siteNav';
import {
  GET_STARTED_BLOCKS,
  GET_STARTED_INTRO,
  GET_STARTED_TAGLINE,
} from '../../components/site/getStartedContent';

// The last block is the requirements list; the rest are what the product does.
const FEATURE_BLOCKS = GET_STARTED_BLOCKS.slice(0, -1);
const REQUIREMENTS = GET_STARTED_BLOCKS[GET_STARTED_BLOCKS.length - 1];

const STEP_LINK = 'text-[#2F5F9E] underline';

/** One numbered capability: "01 / heading" over its short list. */
const FeatureCard = ({ number, heading, items }) => (
  <div className="rounded-[20px] bg-[#EFEFEF] px-6 py-5">
    <p className="text-[13px] font-bold tracking-wide text-[#2F5F9E]">
      {String(number).padStart(2, '0')}
    </p>
    <h3 className="mt-1 text-[17px] font-bold leading-snug text-black md:text-[19px]">{heading}</h3>
    <ul className="mt-3 space-y-2">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-2.5 text-[15px] leading-snug text-black">
          <span aria-hidden="true" className="mt-[7px] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#2F5F9E]" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

/** Page chrome, so checkout reads as part of the site rather than a detour. */
const Shell = ({ children }) => (
  <div className="flex min-h-screen flex-col bg-white">
    <div className="sticky top-0 z-50">
      <SiteHeader />
    </div>
    <main className="flex-1">{children}</main>
    <SiteFooter />
  </div>
);

const GetAccessPage = () => {
  usePageTitle('Get Access');
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscribing, setSubscribing] = useState(null);
  // Billing period toggle: 'month' or 'year'. Starts on 'month' per standard SaaS pattern.
  const [billingPeriod, setBillingPeriod] = useState('month');

  const [searchParams] = useSearchParams();
  const { authenticated, subscription, user, loading: subscriptionLoading } = useSubscription();

  // Fetch available plans
  useEffect(() => {
    fetchPlans();
  }, []);

  // Auto-start checkout when user returns from CIAM login with ?plan=<code>
  useEffect(() => {
    const planParam = searchParams.get('plan');
    if (
      authenticated &&
      planParam &&
      subscription?.status !== 'active' &&
      subscription?.status !== 'past_due' &&
      !subscribing
    ) {
      console.log('[GetAccessPage] Auto-starting checkout for plan:', planParam);
      startCheckout(planParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, subscription, searchParams, subscribing]);

  // Existing subscribers belong on the Download page (/welcome), not here.
  // Decided during render, below, once the subscription has answered - an
  // effect-based redirect let the plan page paint first and sit there until
  // /bff/subscription came back (a second or more on Azure), which read as
  // "signed in and subscribed, still shown the plans" (10-29).
  const hasAccess =
    Boolean(subscription) &&
    (subscription.status === 'active' || subscription.status === 'past_due');

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/bff/plans`, { method: 'GET' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setPlans(data.plans || []);
      setLoading(false);
    } catch (err) {
      console.error('[GetAccessPage] Failed to fetch plans:', err);
      setError('Failed to load plans. Please try again later.');
      setLoading(false);
    }
  };

  /**
   * Start a Stripe Checkout Session for the given plan and redirect to Stripe's hosted page.
   */
  const startCheckout = async (planCode) => {
    setSubscribing(planCode);
    try {
      const response = await fetchWithCsrf(`${API_BASE_URL}/payment/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planCode }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || `HTTP ${response.status}`);
      }

      if (!data.url) {
        throw new Error('Checkout session did not return a redirect URL');
      }

      console.log('[GetAccessPage] Redirecting to Stripe Checkout...');
      window.location.href = data.url;
    } catch (err) {
      console.error('[GetAccessPage] Checkout error:', err);
      alert(`Failed to start checkout: ${err.message}. Please try again.`);
      setSubscribing(null);
    }
  };

  /**
   * Handle "Get Access" click:
   *   - Not authenticated → CIAM login with ?plan=<code> returnUrl
   *   - Authenticated → POST /payment/checkout and redirect to Stripe
   */
  const handleStart = (planCode) => {
    console.log('[GetAccessPage] Start clicked for plan:', planCode);

    if (!authenticated) {
      const returnUrl = encodeURIComponent(`/get-access?plan=${planCode}`);
      window.location.href = `${API_BASE_URL}/auth/login?returnUrl=${returnUrl}`;
      return;
    }

    startCheckout(planCode);
  };

  if (hasAccess) {
    return <Navigate to="/welcome" replace />;
  }

  // Hold the spinner until both the plans and the subscription have answered,
  // so a subscriber never sees the plan page on the way to /welcome.
  if (loading || subscriptionLoading) {
    return (
      <Shell>
        <div className="flex min-h-[60vh] items-center justify-center">
          <div className="text-center">
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-b-2 border-[#2F5F9E]" />
            <p className="mt-4 text-[15px] text-black/70">Loading&hellip;</p>
          </div>
        </div>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell>
        <div className="flex min-h-[60vh] items-center justify-center px-4">
          <div className="text-center">
            <p className="text-[17px] text-[#B02418]">{error}</p>
            <button
              type="button"
              onClick={fetchPlans}
              className="mt-5 rounded-[8px] bg-[#2F5F9E] px-8 py-3 text-[16px] text-white transition-colors hover:bg-[#27508a]"
            >
              Try again
            </button>
          </div>
        </div>
      </Shell>
    );
  }

  // One plan per billing period under the single tier. Matched on billingPeriod
  // alone rather than tier — /bff/plans returns only public plans, and the
  // retired Athena entries are not public, so there is nothing else to pick from.
  const plan = plans.find((p) => p.billingPeriod === billingPeriod);

  const canceledInfo = searchParams.get('checkout') === 'canceled';

  // Setup steps (10-29). The first two are the account and the subscription,
  // each doable from the step itself or from the Get Access button, which does
  // both in one go. Once signed in, step 1 reads as done. The sign-in step
  // names the email when there is one, as the mockup shows.
  const createAccountUrl = `${API_BASE_URL}/auth/login?signup=1&returnUrl=${encodeURIComponent('/get-access')}`;
  const setupSteps = [
    authenticated ? (
      <>Create an account. Done &mdash; you&rsquo;re signed in{user?.email ? ` as ${user.email}` : ''}.</>
    ) : (
      <>
        Create an account by{' '}
        <a href={createAccountUrl} className={STEP_LINK}>clicking here</a>
        {' '}or pressing Get Access.
      </>
    ),
    <>
      Subscribe by{' '}
      {plan ? (
        <button type="button" onClick={() => handleStart(plan.code)} disabled={Boolean(subscribing)} className={STEP_LINK}>
          clicking here
        </button>
      ) : (
        'choosing a plan'
      )}
      {' '}or pressing Get Access.
    </>,
    'Download and install the desktop app.',
    `Sign in with the same email you used to subscribe${user?.email ? ` (${user.email})` : ''}.`,
    'Open the web dashboard from the desktop app when you need advanced features.',
  ];

  return (
    <Shell>
      <div className="mx-auto w-full max-w-[1500px] px-4 pb-16 pt-10 md:px-12">
        {/* Title, in the same outlined box the content pages use */}
        <div className="border border-black/25 px-6 py-6 md:px-10 md:py-8">
          <h1 className="text-[26px] font-bold leading-tight text-black md:text-[40px]">
            Get No / SCAM
          </h1>
        </div>

        {/* 10-07: what you get sits beside the plan. On desktop the product
            story runs down the left and the plan stays in view on the right;
            on a phone the order is intro, plan, then the detail, so the price
            is never more than a screen away. */}
        <div className="mt-8 grid grid-cols-1 gap-x-12 gap-y-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,460px)]">
          <div className="lg:col-start-1 lg:row-start-1">
            <h2 className="text-[24px] font-bold leading-tight text-black md:text-[32px]">
              / {GET_STARTED_TAGLINE}
            </h2>
            <p className="mt-3 max-w-[62ch] text-[16px] leading-relaxed text-black md:text-[18px]">
              {GET_STARTED_INTRO}
            </p>

            {/* Setup steps (10-29): the mockup runs them between the intro and
                the capability cards, so a visitor reads how it works before
                what it does. Same numbered style as the Download page. */}
            <div className="mt-8">
              <h2 className="text-[20px] font-bold text-black md:text-[24px]">/ Setup steps.</h2>
              <ol className="mt-4 space-y-4">
                {setupSteps.map((step, index) => (
                  <li key={index} className="flex items-start gap-4">
                    <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#D9D9D9] text-[14px] font-bold text-black">
                      {index + 1}
                    </span>
                    <span className="text-[15px] leading-snug text-black md:text-[17px]">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          <div className="lg:col-start-2 lg:row-span-2 lg:row-start-1">
            <div className="lg:sticky lg:top-[96px]">
              <p className="text-center text-[15px] leading-relaxed text-black md:text-[16px]">
                One plan, everything included &mdash; the Desktop App and the full web dashboard
                with all protection tools.
              </p>

        {canceledInfo && (
          <div className="mx-auto mt-6 max-w-[640px] border border-[#B58200]/50 bg-[#FDF6E3] px-5 py-4 text-center">
            <p className="text-[15px] text-black">
              Checkout was canceled and no charges were made. Pick a plan below when
              you&rsquo;re ready.
            </p>
          </div>
        )}

        {/* Billing period */}
        <div className="mt-5 flex justify-center">
          <div className="inline-flex border border-black/25">
            {[
              { id: 'month', label: 'Monthly' },
              { id: 'year', label: 'Annual' },
            ].map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setBillingPeriod(option.id)}
                aria-pressed={billingPeriod === option.id}
                className={`px-8 py-2.5 text-[15px] transition-colors md:text-[17px] ${
                  billingPeriod === option.id
                    ? 'bg-[#2F5F9E] text-white'
                    : 'bg-white text-black hover:bg-black/[.04]'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Plan */}
        <div className="mx-auto mt-6 max-w-[520px]">
          {plan ? (
            <PlanCard plan={plan} subscribing={subscribing} onStart={handleStart} />
          ) : (
            <p className="text-center text-[15px] text-black/70">
              No plan is available for {billingPeriod === 'year' ? 'annual' : 'monthly'} billing
              right now.
            </p>
          )}
        </div>

        {/* What happens next — the step the meeting flagged as unclear */}
        <div className="mx-auto mt-6 max-w-[640px] text-center">
          {authenticated ? (
            <p className="text-[15px] text-black/75">
              You&rsquo;re signed in. Choosing a plan takes you straight to secure checkout.
            </p>
          ) : (
            <>
              <p className="text-[15px] text-black/75">
                Choosing a plan takes you to create your account, then on to secure checkout.
              </p>
              <p className="mt-3 text-[15px] text-black/75">
                Already have an account?{' '}
                <Link to={ROUTES.signIn} className="text-[#2F5F9E] underline">
                  Sign in
                </Link>
                .
              </p>
            </>
          )}
        </div>
            </div>
          </div>

          {/* What it does, then what it needs. */}
          <div className="lg:col-start-1 lg:row-start-2">
            <h2 className="text-[20px] font-bold text-black md:text-[24px]">/ What you get.</h2>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {FEATURE_BLOCKS.map((block, index) => (
                <FeatureCard key={block.heading} number={index + 1} {...block} />
              ))}
            </div>

            <div className="mt-6 rounded-[20px] border border-black/20 px-6 py-5">
              <h2 className="text-[18px] font-bold text-black md:text-[20px]">
                Before you start.
              </h2>
              <ul className="mt-3 grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
                {REQUIREMENTS.items.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-[15px] leading-snug text-black">
                    <span aria-hidden="true" className="mt-[7px] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-black" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Shell>
  );
};

/**
 * Presentational card for one plan.
 */
const PlanCard = ({ plan, subscribing, onStart }) => {
  const isBusy = subscribing === plan.code;

  return (
    <div className="border border-black/25 px-6 py-8 md:px-9">
      <div className="text-center">
        <h2 className="text-[20px] font-bold text-black md:text-[24px]">{plan.label}</h2>
        <div className="mt-2 text-[44px] font-bold leading-none text-black md:text-[56px]">
          {plan.priceLabel}
        </div>
        {plan.description && (
          <p className="mt-3 text-[15px] text-black/70">{plan.description}</p>
        )}
      </div>

      <div className="my-7 h-px w-full bg-black/20" />

      {/* 10-07: the "What’s included" feature list is replaced by a photo;
          the line above the card already says the one plan has everything. */}
      <img
        src="/images/site/get-access/couple-at-laptop.jpeg"
        alt="An older couple smiling at each other over a laptop"
        className="block w-full"
      />

      <button
        type="button"
        onClick={() => onStart(plan.code)}
        disabled={isBusy}
        className="mt-8 w-full rounded-[8px] bg-[#2F5F9E] px-6 py-4 text-[17px] text-white transition-colors hover:bg-[#27508a] disabled:cursor-not-allowed disabled:opacity-60 md:text-[19px]"
      >
        {isBusy ? (
          <span className="flex items-center justify-center gap-3">
            <span className="h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
            Redirecting to checkout&hellip;
          </span>
        ) : (
          'Get Access'
        )}
      </button>

      <p className="mt-3 text-center text-[13px] text-black/60">
        Secure checkout by Stripe. Cancel any time.
      </p>
    </div>
  );
};

export default GetAccessPage;
